import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Button,
  Container,
  Fade,
  Avatar,
  CircularProgress,
  Alert,
  Typography,
  Paper,
  Chip,
  IconButton,
} from '@mui/material';
import VerifiedIcon from '@mui/icons-material/Verified';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { AiActions } from '../../../Services/_actions/Ai/ai.actions.ts';
import { useRecoilValue } from 'recoil';
import { userInformationAtom } from '../../../Services/_states/auth.js';
import Header from './Header.tsx';
import SelectionFlow, { TopicSubtopicWithPrereqs } from './Selectionflow.tsx';
import QuestionBankView from './Questionbankview.tsx';
import GeneratedQuestionsTable from './Generatedquestionstable.tsx';
import Sidebar from './Sidebar.tsx';
import { useWebSocketAiContext } from '../../../utils/WebSocketContext.tsx';

// ─── Helper: flatten a prerequisite tree into an ordered array of names ────────
interface PrerequisiteNode {
  id: string;
  name: string;
  children: PrerequisiteNode[];
}

const flattenPrerequisites = (nodes: PrerequisiteNode[]): string[] => {
  const result: string[] = [];
  const traverse = (node: PrerequisiteNode) => {
    result.push(node.name);
    node.children.forEach(traverse);
  };
  nodes.forEach(traverse);
  return result;
};

/**
 * Converts the rich TopicSubtopicWithPrereqs structure into the API payload shape:
 *
 * {
 *   "Motion": {
 *     "Distance and displacement": ["Reference point (starting line)", "Concept of path length", ...],
 *     "Velocity and Speed": [...]
 *   },
 *   ...
 * }
 */
const buildTopicSubtopicPayload = (
  selected: TopicSubtopicWithPrereqs
): Record<string, Record<string, string[]>> => {
  const payload: Record<string, Record<string, string[]>> = {};
  Object.entries(selected).forEach(([topic, subtopicEntries]) => {
    payload[topic] = {};
    subtopicEntries.forEach(({ subtopic, prerequisites }) => {
      payload[topic][subtopic] = flattenPrerequisites(prerequisites);
    });
  });
  return payload;
};

// ─── NEW: pagination page size ─────────────────────────────────────────────────
const QUESTIONS_LIMIT = 20;

const QuestionBankGenerator = () => {
  const actions = AiActions();
  const loggedInUser = useRecoilValue(userInformationAtom);
  const mobile = loggedInUser?.mobile;
  const userId = loggedInUser?.user_id || loggedInUser?.id || '';

  // Selection States
  const [selectedBoard, setSelectedBoard] = useState('');
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');

  // ─── UPDATED: now holds subtopic + prerequisites ──────────────────────────
  const [selectedTopicSubtopics, setSelectedTopicSubtopics] = useState<TopicSubtopicWithPrereqs>({});

  const [selectedDifficulty, setSelectedDifficulty] = useState<string[]>([]);
  const [selectedQuestionTypes, setSelectedQuestionTypes] = useState<string[]>([]);
  const [selectedQuestionCount, setSelectedQuestionCount] = useState('');

  // UI States
  const [isGenerating, setIsGenerating] = useState(false);
  const [questionsGenerated, setQuestionsGenerated] = useState(false);
  const [viewingQuestions, setViewingQuestions] = useState(false);
  const [viewingGeneratedTable, setViewingGeneratedTable] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // ─── NEW: waiting for socket response after generate ──────────────────────
  const [waitingForSocket, setWaitingForSocket] = useState(false);

  // Data States
  const [knowledgeMapData, setKnowledgeMapData] = useState<any>(null);
  const [questionBankData, setQuestionBankData] = useState<any>(null);
  const [generatedQuestionsData, setGeneratedQuestionsData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [loadingQuestions, setLoadingQuestions] = useState(false);

  // ─── NEW: pagination state ─────────────────────────────────────────────────
  const [allQuestions, setAllQuestions] = useState<any[]>([]);
  const [pagination, setPagination] = useState<any>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentOffset, setCurrentOffset] = useState(0);

  const { state } = useWebSocketAiContext();

  // ─── NEW: normalise any response into allQuestions + pagination ────────────
  // Real API shape:
  // { question_bank_id, questions: [...], duplicates_skipped, total_generated, total_stored, status }
  // questions[i] = { question_text, topic, subtopic, difficulty, question_type,
  //   options: [{ text, is_correct, correct_reason?, incorrect_reason? }], is_duplicate? }
  const initQuestionsState = (data: any) => {
    // questions may live at data.questions, data.data, or data itself if already an array
    const questions: any[] = Array.isArray(data)
      ? data
      : (data?.questions ?? data?.data ?? []);
    const pag = data?.pagination || null;
    setAllQuestions(questions);
    setPagination(pag);
    setCurrentOffset(pag ? pag.offset + questions.length : questions.length);
    setGeneratedQuestionsData(data);
  };

  useEffect(() => {
    if (!state?.questionsGenerated) return;
    const Data = state?.questionsGenerated;
    setQuestionsGenerated(true);
    setViewingGeneratedTable(true);
    setWaitingForSocket(false);   // ─── socket arrived, stop loading ──────
    setIsGenerating(false);       // ─── ensure generate button re-enables ──
    initQuestionsState(Data);
  }, [state?.questionsGenerated]);

  useEffect(() => {
    getKnowledgeMap();
  }, []);

  const getKnowledgeMap = async () => {
    try {
      setLoading(true);
      const response = await actions.getKnowledgeList();
      setKnowledgeMapData(response);
    } catch (error) {
      console.error('Error fetching knowledge map:', error);
    } finally {
      setLoading(false);
    }
  };

  // ─── NEW: load next page (offset pagination, limit=20) ────────────────────
  const handleLoadMoreQuestions = async () => {
    if (!generatedQuestionsData?.question_bank_id || loadingMore) return;
    setLoadingMore(true);
    try {
      const response = await actions.getGeneratedQuestions({
        question_bank_id: generatedQuestionsData.question_bank_id,
        offset: currentOffset,
        limit: QUESTIONS_LIMIT,
      });
      const newQuestions = response?.questions || response?.data || [];
      const newPagination = response?.pagination || null;
      setAllQuestions((prev) => [...prev, ...newQuestions]);
      setPagination(newPagination);
      setCurrentOffset((prev) => prev + newQuestions.length);
    } catch (error) {
      console.error('Error loading more questions:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  const fetchAllQuestions = async () => {
    try {
      setLoadingQuestions(true);
      const response = await actions.getQuestions({});
      setQuestionBankData(response);
      setViewingQuestions(true);
      setLoadingQuestions(false);
    } catch (error) {
      console.error('Error fetching all questions:', error);
      setLoadingQuestions(false);
    }
  };

  // const handleViewQuestionBank = async () => {
  //   setLoadingQuestions(true);
  //   try {
  //     const selectedKnowledgeItem = knowledgeMapData.find(
  //       (item: any) =>
  //         item?.board === selectedBoard &&
  //         item?.class === selectedClass &&
  //         item?.subject === selectedSubject
  //     );
  //     const params = {
  //       board: selectedBoard,
  //       class: selectedClass,
  //       subject: selectedSubject,
  //       campaign_id: selectedKnowledgeItem?.campaign_id || '',
  //       lead_form_id: selectedKnowledgeItem?.lead_form_id || '',
  //       user_id: userId,
  //       phone_number: mobile || '',
  //     };
  //     const response = await actions.getQuestions(params);
  //     setQuestionBankData(response);
  //     setViewingQuestions(true);
  //     setLoadingQuestions(false);
  //   } catch (error) {
  //     console.error('Error fetching questions:', error);
  //     setLoadingQuestions(false);
  //   }
  // };

  const handleViewQuestionBank = () => {
    setViewingQuestions(true);
  };
  const handleGenerateQuestions = async () => {
    setIsGenerating(true);
    setWaitingForSocket(true);   // ─── start socket-wait loading indicator ──

    const selectedKnowledgeItem = knowledgeMapData.find(
      (item: any) =>
        item.board === selectedBoard &&
        item.class === selectedClass &&
        item.subject === selectedSubject
    );

    // ─── Build the enriched topic_sub_topic payload ────────────────────────
    // Shape sent to API:
    // {
    //   "Motion": {
    //     "Distance and displacement": ["Reference point (starting line)", "Concept of path length", ...],
    //     "Velocity and Speed": ["Rate of change logic", ...]
    //   }
    // }
    const topicSubtopicPayload = buildTopicSubtopicPayload(selectedTopicSubtopics);

    const payload = {
      board: selectedBoard,
      class: selectedClass,
      subject: selectedSubject,
      topic_sub_topic: topicSubtopicPayload,         // ← enriched with prerequisites
      difficulty: selectedDifficulty,
      question_types: selectedQuestionTypes.join(','),
      question_count: selectedQuestionCount,
      campaign_id: selectedKnowledgeItem?.campaign_id || '',
      lead_form_id: selectedKnowledgeItem?.lead_form_id || '',
      user_id: userId,
      phone_number: mobile || '',
      question_generator_instruction: selectedKnowledgeItem?.question_generator_instruction || '',
    };

    try {
      const response = await actions.generateAndStoreQuestions(payload);

      // Real success shape: { question_bank_id, questions: [...], status, ... }
      // question_bank_id is the reliable success signal
      if (response?.question_bank_id) {
        initQuestionsState(response);
        setViewingGeneratedTable(true);
        setWaitingForSocket(false);
      }
      // if no direct response (async flow), keep waitingForSocket=true until socket fires

      setIsGenerating(false);
    } catch (error) {
      console.error('Error generating questions:', error);
      setIsGenerating(false);
      setWaitingForSocket(false);
    }
  };

  const handleVerifyQuestions = async () => {
    if (!generatedQuestionsData?.question_bank_id) return;
    setIsGenerating(true);

    const selectedKnowledgeItem = knowledgeMapData.find(
      (item: any) =>
        item.board === selectedBoard &&
        item.class === selectedClass &&
        item.subject === selectedSubject
    );

    const payload = {
      question_bank_id: generatedQuestionsData?.question_bank_id || '',
      verification_instruction: selectedKnowledgeItem?.verification_instruction || '',
    };

    try {
      const response = await actions.verifyQuestions(payload);
      setIsGenerating(false);
      setQuestionsGenerated(true);
      setViewingGeneratedTable(false);
    } catch (error) {
      console.error('Error verifying questions:', error);
      setIsGenerating(false);
    }
  };

  const handleVerify = async () => {
    if (!generatedQuestionsData?.question_bank_id) return;
    setIsGenerating(true);

    const payload = {
      question_bank_id: generatedQuestionsData?.question_bank_id || '',
    };

    try {
      const response = await actions.verifyMe(payload);
      setIsGenerating(false);
      setQuestionsGenerated(true);
      setViewingGeneratedTable(false);
    } catch (error) {
      console.error('Error verifying questions:', error);
      setIsGenerating(false);
    }
  };

  const resetAll = () => {
    setSelectedBoard('');
    setSelectedClass('');
    setSelectedSubject('');
    setSelectedTopicSubtopics({});
    setSelectedDifficulty([]);
    setSelectedQuestionTypes([]);
    setSelectedQuestionCount('');
    setQuestionsGenerated(false);
    setViewingQuestions(false);
    setViewingGeneratedTable(false);
    setQuestionBankData(null);
    setGeneratedQuestionsData(null);
    // ─── NEW: reset pagination ─────────────────────────────────────────────
    setAllQuestions([]);
    setPagination(null);
    setCurrentOffset(0);
    setWaitingForSocket(false);
  };

  const canGenerate =
    selectedBoard &&
    selectedClass &&
    selectedSubject &&
    Object.keys(selectedTopicSubtopics)?.length > 0 &&
    selectedDifficulty?.length > 0 &&
    selectedQuestionTypes?.length > 0 &&
    selectedQuestionCount;

  // Loading state
  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f5f7fa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={48} />
          <Typography variant="body1" sx={{ mt: 2, color: '#666' }}>
            Loading knowledge map...
          </Typography>
        </Box>
      </Box>
    );
  }

  // Error state
  if (!knowledgeMapData || knowledgeMapData?.length === 0) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f5f7fa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Container maxWidth="sm">
          <Alert severity="error">
            <Typography variant="body1">
              Unable to load knowledge map data. Please try refreshing the page.
            </Typography>
          </Alert>
        </Container>
      </Box>
    );
  }

  // ─── NEW: waiting for socket loading overlay ──────────────────────────────
  if (waitingForSocket && !viewingGeneratedTable) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f5f7fa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={52} />
          <Typography variant="body1" sx={{ mt: 2, color: '#555', fontWeight: 500 }}>
            Generating Questions...
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5, color: '#999', fontSize: 12 }}>
            This may take a moment. Please wait.
          </Typography>
        </Box>
      </Box>
    );
  }

  // Generated Questions Table View
  if (viewingGeneratedTable && generatedQuestionsData?.question_bank_id) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f5f7fa' }}>
        <Header
          title="Generated Questions - Review & Verify"
          onBack={() => setViewingGeneratedTable(false)}
          actions={
            <>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setViewingGeneratedTable(false)}
                startIcon={<ArrowBackIcon />}
                sx={{
                  color: '#fff', borderColor: '#fff', textTransform: 'none', fontSize: 11,
                  '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.1)' },
                }}
              >
                Back
              </Button>

              {/* ─── AI Verify with loading spinner ── */}
              <Button
                variant="contained"
                size="small"
                onClick={handleVerifyQuestions}
                disabled={isGenerating}
                startIcon={isGenerating ? <CircularProgress size={13} color="inherit" /> : undefined}
                sx={{
                  bgcolor: '#fff', color: 'primary.main', textTransform: 'none', fontSize: 11,
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                }}
              >
                {isGenerating ? 'Verifying...' : 'AI Verify'}
              </Button>

              {/* ─── Manual Verify with loading spinner ── */}
              <Button
                variant="contained"
                size="small"
                onClick={handleVerify}
                disabled={isGenerating}
                startIcon={isGenerating ? <CircularProgress size={13} color="inherit" /> : undefined}
                sx={{
                  bgcolor: '#fff', color: 'primary.main', textTransform: 'none', fontSize: 11,
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                }}
              >
                {isGenerating ? 'Verifying...' : 'Manual Verify'}
              </Button>
            </>
          }
        />

        <Container maxWidth="xl" sx={{ pt: 9, pb: 4 }}>
          <Fade in={true}>
            <Box>
              {/* fixed metadata bar */}
              <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                  {selectedBoard && <Chip label={`Board: ${selectedBoard}`} size="small" sx={{ fontSize: 10 }} />}
                  {selectedClass && <Chip label={`Class: ${selectedClass}`} size="small" sx={{ fontSize: 10 }} />}
                  {selectedSubject && <Chip label={`Subject: ${selectedSubject}`} size="small" sx={{ fontSize: 10 }} />}
                  <Chip
                    label={`Questions: ${allQuestions.length}`}
                    size="small"
                    sx={{ bgcolor: 'primary.main', color: '#fff', fontSize: 10 }}
                  />
                  {/* duplicates_skipped from real API response */}
                  {generatedQuestionsData?.duplicates_skipped > 0 && (
                    <Chip
                      label={`${generatedQuestionsData.duplicates_skipped} duplicate${generatedQuestionsData.duplicates_skipped > 1 ? 's' : ''} skipped`}
                      size="small"
                      sx={{ bgcolor: '#FFF3E0', color: '#E65100', fontSize: 10 }}
                    />
                  )}
                  {/* pagination loaded-so-far */}
                  {pagination && allQuestions.length < pagination.total && (
                    <Chip
                      label={`Loaded: ${allQuestions.length} of ${pagination.total}`}
                      size="small"
                      sx={{ fontSize: 10 }}
                    />
                  )}
                </Box>
              </Paper>

              {/* ─── NEW: passes allQuestions + pagination to table ── */}
              <GeneratedQuestionsTable
                questions={allQuestions}
                pagination={pagination}
                loadingMore={loadingMore}
                onLoadMore={handleLoadMoreQuestions}
              />
            </Box>
          </Fade>
        </Container>
      </Box>
    );
  }

  // Question Bank View
  if (viewingQuestions) {
    return (
      <QuestionBankView
        selectedBoard={selectedBoard}
        selectedClass={selectedClass}
        selectedSubject={selectedSubject}
        onBack={() => setViewingQuestions(false)}
        knowledgeMapData={knowledgeMapData}
        onClearFilters={() => {
          setSelectedBoard('');
          setSelectedClass('');
          setSelectedSubject('');
          fetchAllQuestions();
        }}
      />
    );
  }

  // Success View
  if (questionsGenerated) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f5f7fa' }}>
        <Header title="Question Bank Generator" />

        <Container maxWidth="md" sx={{ pt: 9, pb: 4 }}>
          <Fade in={true}>
            <Paper elevation={2} sx={{ p: { xs: 3, md: 4 }, borderRadius: 2, textAlign: 'center' }}>
              <Avatar sx={{ width: 56, height: 56, bgcolor: 'success.main', mb: 2, mx: 'auto' }}>
                <VerifiedIcon sx={{ fontSize: 32 }} />
              </Avatar>

              <Typography variant="h5" sx={{ mb: 1.5, fontSize: { xs: 18, md: 20 } }}>
                Questions Generated & Verified Successfully!
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 3, fontSize: 12 }}>
                {selectedQuestionCount} questions generated, verified, and stored
              </Typography>

              <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
                <Typography variant="body2" sx={{ mb: 0.5, fontSize: 11, fontWeight: 600 }}>
                  Completed Steps:
                </Typography>
                <Typography variant="body2" sx={{ fontSize: 10, mb: 0.3 }}>✓ Questions generated using AI</Typography>
                <Typography variant="body2" sx={{ fontSize: 10, mb: 0.3 }}>✓ Questions verified using multiple AI models</Typography>
                <Typography variant="body2" sx={{ fontSize: 10, mb: 0.3 }}>✓ Authenticity check completed</Typography>
                <Typography variant="body2" sx={{ fontSize: 10 }}>✓ Stored in question bank</Typography>
              </Alert>

              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  onClick={resetAll}
                  sx={{ textTransform: 'none', fontSize: 12, px: 3, py: 0.8, borderRadius: 1.5 }}
                >
                  Generate More
                </Button>

                <Button
                  variant="outlined"
                  onClick={handleViewQuestionBank}
                  disabled={loadingQuestions}
                  sx={{ textTransform: 'none', fontSize: 12, px: 3, py: 0.8, borderRadius: 1.5 }}
                >
                  {loadingQuestions ? 'Loading...' : 'View Question Bank'}
                </Button>
              </Box>
            </Paper>
          </Fade>
        </Container>
      </Box>
    );
  }

  // Main Selection Flow
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f7fa' }}>
      <Header
        title="Question Bank Generator"
        subtitle="Admin Panel"
        onMenuClick={() => setSidebarOpen(true)}
        actions={
          <Button
            variant="outlined"
            size="small"
            onClick={handleViewQuestionBank}
            disabled={loadingQuestions}
            sx={{
              color: '#fff', borderColor: '#fff', textTransform: 'none', fontSize: 11, px: 2,
              '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.1)' },
            }}
          >
            {loadingQuestions ? 'Loading...' : 'View All Questions'}
          </Button>
        }
      />
      <Box sx={{ display: 'flex' }}>
        <Sidebar
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          selectedBoard={selectedBoard}
          selectedClass={selectedClass}
          selectedSubject={selectedSubject}
          selectedTopicSubtopics={selectedTopicSubtopics}
          selectedDifficulty={selectedDifficulty}
          selectedQuestionTypes={selectedQuestionTypes}
          selectedQuestionCount={selectedQuestionCount}
          canGenerate={canGenerate}
          isGenerating={isGenerating}
          onGenerate={handleGenerateQuestions}
        />
        <Box sx={{ p: 0 }}>
          <Container maxWidth="lg">
            <SelectionFlow
              knowledgeMapData={knowledgeMapData}
              selectedBoard={selectedBoard}
              selectedClass={selectedClass}
              selectedSubject={selectedSubject}
              selectedTopicSubtopics={selectedTopicSubtopics}
              selectedDifficulty={selectedDifficulty}
              selectedQuestionTypes={selectedQuestionTypes}
              selectedQuestionCount={selectedQuestionCount}
              setSelectedBoard={setSelectedBoard}
              setSelectedClass={setSelectedClass}
              setSelectedSubject={setSelectedSubject}
              setSelectedTopicSubtopics={setSelectedTopicSubtopics}
              setSelectedDifficulty={setSelectedDifficulty}
              setSelectedQuestionTypes={setSelectedQuestionTypes}
              setSelectedQuestionCount={setSelectedQuestionCount}
              canGenerate={canGenerate}
              isGenerating={isGenerating}
              onGenerate={handleGenerateQuestions}
            />
          </Container>
        </Box>
      </Box>
    </Box>
  );
};

export default QuestionBankGenerator;
